import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../sharedFeatures/services/notification.service';
import { CurrentUserService } from '../../../../sharedFeatures/services/current-user.service';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-login-callback',
  templateUrl: './login-callback.component.html',
  styleUrls: ['./login-callback.component.scss'],
})
export class LoginCallbackComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  isProcessing: boolean = true;
  errorMessage: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private currentUserService: CurrentUserService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.handleOAuthCallback();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Handle OAuth callback from Microsoft
   */
  private handleOAuthCallback(): void {
    // Get authorization code and state from query parameters
    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      const error = params['error'];
      const errorDescription = params['error_description'];
      const state = params['state']; // returnUrl

      if (error) {
        // OAuth error occurred
        this.errorMessage = this.translateService.instant(
          'users.loginCallback.microsoftFailed'
        );
        this.isProcessing = false;
        this.notificationService.showError(this.errorMessage, '');
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
        return;
      }

      if (!code) {
        // No authorization code received
        this.errorMessage = this.translateService.instant(
          'users.loginCallback.noCode'
        );
        this.isProcessing = false;
        this.notificationService.showError(this.errorMessage, '');
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
        return;
      }

      // Call backend API to exchange code and login user
      this.loginWithMicrosoftCode(code, state);
    });
  }

  /**
   * Call backend API to exchange code for token and login user
   */
  private loginWithMicrosoftCode(code: string, returnUrl: string): void {
    this.subscriptions.push(
      this.loginService.loginWithMicrosoft(code, returnUrl).subscribe(
        (userDetails: any) => {
          this.isProcessing = false;

          // Store user data
          this.currentUserService.setCurrentUser(userDetails);

          // Store login credentials temporarily in sessionStorage
          // (Microsoft login doesn't need password, but we mark it as AD login)
          sessionStorage.setItem(
            'tempLoginCredentials',
            JSON.stringify({
              userName: userDetails.userName,
              fromActiveDirectory: true,
            })
          );

          // Show success message
          this.notificationService.showSuccessTranslated(
            'users.loginCallback.successMessage',
            ''
          );

          // Navigate to module selection or return URL
          const finalReturnUrl =
            returnUrl || this.currentUserService.getReturnUrl();
          if (finalReturnUrl) {
            this.router.navigate(['/modules'], {
              queryParams: { returnUrl: finalReturnUrl },
            });
          } else {
            this.router.navigate(['/modules']);
          }
        },
        (error: any) => {
          this.isProcessing = false;

          let errorMsg = this.translateService.instant(
            'users.loginCallback.microsoftFailed'
          );
          if (error.status === 400 || error.status === 404) {
            errorMsg = this.translateService.instant(
              'users.loginCallback.notRegistered'
            );
          }

          this.errorMessage = errorMsg;
          this.notificationService.showError(errorMsg, '');

          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 3000);
        }
      )
    );
  }
}
